/**
 * Shared utility for parsing and handling bracketed text
 * Used by both TextEditor placeholder and BracketedText plugin
 */

export interface BracketedTextPart {
  endIndex: number;
  isBracketed: boolean;
  startIndex: number;
  text: string;
}

/**
 * Parse text and identify bracketed content with positions
 * @param text - The text to parse
 * @returns Array of parts with their positions and bracketed status
 */
export const parseBracketedText = (text: string): BracketedTextPart[] => {
  // Early return if no brackets to avoid regex execution
  if (!text.includes("[")) {
    return [
      {
        text,
        isBracketed: false,
        startIndex: 0,
        endIndex: text.length,
      },
    ];
  }

  const regex = /\[[^\]]+\]/g;
  const parts: BracketedTextPart[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the bracket
    if (match.index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, match.index),
        isBracketed: false,
        startIndex: lastIndex,
        endIndex: match.index,
      });
    }

    // Add the bracketed text
    parts.push({
      text: match[0],
      isBracketed: true,
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last bracket
  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      isBracketed: false,
      startIndex: lastIndex,
      endIndex: text.length,
    });
  }

  return parts;
};

/**
 * CSS classes for bracketed text styling
 * Matches the styling used in BracketedText plugin
 */
export const BRACKETED_TEXT_CLASSES =
  "bracketed-text py-1 px-1 border border-[#B743C6] border-opacity-20 rounded-sm whitespace-nowrap text-[#B743C6]";

/**
 * Data attribute for bracketed text
 */
export const BRACKETED_TEXT_DATA_ATTRIBUTE = "data-bracketed-text";
